import React, { FC, useEffect } from "react";

import { Box, Button, SelectChangeEvent } from "@mui/material";
import {
  DataGridPro,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridToolbarContainer,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { ThemeProvider } from "@mui/material/styles";

import themeInarix from "../../../StylesMuiInarix";
import CreateLocationsColumnsRows from "./FetchColumnsConfig";
import { useTranslation } from "react-i18next";
import { State } from "../../../../redux/slices";
import { useDispatch, useSelector } from "react-redux";
import { MenuCreateLocation } from "../../CreateLocation";
import { useNavigate } from "react-router-dom";
import { useUnsavedChanges } from "../../../../hooks/useUnsavedChanges";
import {
  CreateLocationStep,
  initialState,
  resetStateCreatLocUsers,
  setColumns,
  setCreateLocationStep,
  setCreateUsersLoc,
  setMaximumNumberFromUserCreationsLoc,
  setRowsAlpha,
  setTryToCreate,
  setUserConfigRows,
} from "../../../../redux/slices/location/createLocation";
import AddIcon from "@mui/icons-material/Add";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { createLocation } from "../../../../redux/actions/locations/locationsAsyncActions";
import { TokenPayload } from "../../../../redux/actions/authentication/authentication";
import jwt from "jsonwebtoken";
import Alert from "@mui/material/Alert";
import { infoValidator } from "../../../userGroups/creationUsers/managerUsers/managerUsersPartsOfTable/UsersManagersConfigTable";
import { setVerifyAllIsCreated } from "../../../../redux/slices/users/createUsers";
import { SiteUsersConfigRows } from "../../../../redux/actions/users/userTypes";
import { selectLocale } from "../../../../redux/slices/layout";

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  return {
    setRowsAlpha2: (value: SiteUsersConfigRows[]) => {
      dispatch(setRowsAlpha(value));
    },

    setConfigRow: (value: SiteUsersConfigRows[]) => {
      dispatch(setUserConfigRows(value));
    },

    setSteps: (value: CreateLocationStep) => {
      dispatch(setCreateLocationStep(value));
    },
    cancel: () => {
      dispatch(resetStateCreatLocUsers());
    },
    setColumnIndex: (columns: any) => {
      dispatch(setColumns(columns));
    },

    setStep: (value: CreateLocationStep) => {
      dispatch(setCreateLocationStep(value));
    },
    createLocations: (adminId: string, token: string, realmId: number) => {
      dispatch(createLocation({ adminId, token, realmId }));
    },
    setMaximumNumber: (value: number, removedUsernames: number) => {
      if (removedUsernames >= 1) {
        return () => {};
      } else {
        dispatch(setMaximumNumberFromUserCreationsLoc(value));
      }
    },

    resetCreateUsersValues: () => {
      dispatch(setCreateUsersLoc(initialState.createUsers));
    },

    setVerifyAllIsCreatedDefault: () => {
      dispatch(
        setVerifyAllIsCreated({
          createdUsers: false,
          linkActions: false,
          usersCreated: 0,
        }),
      );
    },

    tryToCreate: (value: boolean) => {
      dispatch(setTryToCreate(value));
    },
  };
};

export const StepConfigCreateUsers: FC<any> = () => {
  const apiRef = useGridApiRef();
  const [t] = useTranslation("global");
  const { user, companyName } = useSelector(
    (state: State) => state.Authentication,
  );
  const navigate = useNavigate();

  const {
    setSteps,
    cancel,
    setColumnIndex,
    setRowsAlpha2,
    createLocations,
    setMaximumNumber,
    tryToCreate,
    setConfigRow,
    resetCreateUsersValues,
    setVerifyAllIsCreatedDefault,
  } = SetActions();
  const {
    createLocationStep,
    usersConfigRows,
    columns,
    rowsAlpha,
    errorCreateLocations,
    errorCreateUsers,
    createUsers,
    MaximumNumberUsernamesCreationsLoc,
    tryToCreateSomething,
    multiLocationLoading,
  } = useSelector((state: State) => state.CreateLocationReducer);
  const { principalInfoGT, principalInfoGTCopy } = useSelector(
    (state: State) => state.GrainWiseEditionReducer,
  );
  const { multiLocationCreatedSuccess } = useSelector(
    (state: State) => state.CreateLocationReducer,
  );

  const locale = useSelector(selectLocale);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {},
  );

  const token = user?.token ?? "";
  const { userId, realmId }: TokenPayload = jwt.decode(token) as TokenPayload;

  const companyNameUsers = companyName ?? "error";
  const r = usersConfigRows.filter(
    (elem) => !rowsAlpha.find(({ username }) => elem.username === username),
  );

  const removedUsernames = r.map((values) => values.username);

  const managingUserNames = () => {
    if (removedUsernames.length >= 1) {
      return removedUsernames[0];
    } else {
      return `${companyNameUsers}-${MaximumNumberUsernamesCreationsLoc + 1}`;
    }
  };

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setRowsAlpha2(rowsAlpha.filter((row) => row.id !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rowsAlpha.find((row) => row.id === id);
    if (editedRow!.isNew) {
      setRowsAlpha2(rowsAlpha.filter((row) => row.id !== id));
    }
  };

  const toggleNomad = (id: GridRowId) => () => {
    setRowsAlpha2(
      rowsAlpha.map((row) =>
        row.id === id
          ? {
              ...row,
              nomadUser: !row.nomadUser,
              assignedSite: !row.nomadUser
                ? { name: "Nomad", id: "nomad" }
                : { name: t("Common_options.select") },
            }
          : row,
      ),
    );
  };

  const handleChange = (
    locationID: string,
    locationName: string,
    id: number,
  ) => {
    setRowsAlpha2(
      rowsAlpha.map((row) =>
        row.id === id
          ? {
              ...row,
              assignedSite: {
                name: locationName,
                id: locationID,
              },
              nomadUser: false,
            }
          : row,
      ),
    );
  };

  const handleChangeActions = (event: SelectChangeEvent<any>, id: number) => {
    const {
      target: { value },
    } = event;

    setRowsAlpha2(
      rowsAlpha.map((row) =>
        row.id === id
          ? {
              ...row,
              assignedAction: value === "string" ? value.split(",") : value,
            }
          : row,
      ),
    );
  };

  const handleChangeUsername = (event: any, id: GridRowId) => {
    const {
      target: { value },
    } = event;

    setRowsAlpha2(
      rowsAlpha.map((row: any) =>
        row.id === id
          ? {
              ...row,
              username: value === "string" ? value.split(",") : value,
            }
          : row,
      ),
    );
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };
    setRowsAlpha2(
      rowsAlpha.map((row) =>
        row.id === newRow.id ? updatedRow : row,
      ) as SiteUsersConfigRows[],
    );
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const { rawColumns } = CreateLocationsColumnsRows(
    columns,
    rowModesModel,
    handleSaveClick,
    handleCancelClick,
    handleEditClick,
    handleDeleteClick,
    handleChangeUsername,
    toggleNomad,
    handleChange,
    handleChangeActions,
    usersConfigRows,
  );

  if (createUsers.numberOfUserToCreate === 0) {
    navigate("/");
  }

  useEffect(() => {
    tryToCreate(false);
    if (rowsAlpha.length === 0) {
      setRowsAlpha2(usersConfigRows);
    }

    setSteps({ ...createLocationStep, place: "configs" });

    window.onbeforeunload = function () {
      window.setTimeout(function () {
        window.location = "/create-locations/" as any;
      }, 0);
      window.onbeforeunload = null; // necessary to prevent infinite loop
    };
  }, []);

  const editToolbar = () => {
    const handleClick = () => {
      const id = Math.random();
      setMaximumNumber(
        MaximumNumberUsernamesCreationsLoc + 1,
        removedUsernames.length,
      );
      setConfigRow([
        ...usersConfigRows,
        {
          id,
          username: managingUserNames(),
          assignedSite: { name: t("Common_options.select") },
          assignedAction: [],
          nomadUser: false,
          isNew: true,
        },
      ]);

      setRowsAlpha2([
        ...rowsAlpha,
        {
          id,
          username: managingUserNames(),
          assignedSite: { name: t("Common_options.select") },
          assignedAction: [],
          nomadUser: false,
          isNew: true,
        },
      ]);

      setRowModesModel((oldModel) => ({
        ...oldModel,
        [id]: { mode: GridRowModes.Edit, fieldToFocus: "username" },
      }));
    };

    return (
      <GridToolbarContainer>
        <Button
          color="primary"
          startIcon={<AddIcon />}
          disabled={rowsAlpha.length >= 100 || multiLocationLoading}
          onClick={handleClick}
        >
          {t("Create_site.config_users.add_user")}
        </Button>
      </GridToolbarContainer>
    );
  };

  const managingError = () => {
    if (errorCreateLocations) {
      return (
        <Alert severity="error">{JSON.stringify(errorCreateLocations)}</Alert>
      );
    }
    if (errorCreateUsers) {
      return <Alert severity="error">{JSON.stringify(errorCreateUsers)}</Alert>;
    }

    if (!errorCreateLocations && !errorCreateUsers) {
      return "";
      // disable until we define the export of user created
      // <Alert severity="info">{t("Create_site.config_users.user_pass")}</Alert>
    }
  };

  // Validator of empty values etc..

  const siteValidator = (name: string) => {
    if (name === t("Common_options.select") || name === "") {
      return false;
    }
  };
  const theUserIsGood = () => {
    const transformToValidate = rowsAlpha.map((validateRows, index) => ({
      username: validateRows.username,
      assignedSite: validateRows.assignedSite,
      assignedAction: validateRows.assignedAction,
      nomadUser: validateRows.nomadUser,
      isTheAssignedSiteGood: siteValidator(validateRows.assignedSite.name),
      isTheassignedActionGood: !infoValidator(validateRows.assignedAction),
      rowPosition: index + 1,
    }));
    return transformToValidate;
  };

  const encounterErrorSites = theUserIsGood().filter(
    (rows) => rows.isTheAssignedSiteGood === false,
  );

  const encounterErrorActions = theUserIsGood().filter(
    (rows) => rows.isTheassignedActionGood === false,
  );

  const totalErrors = encounterErrorSites?.concat(encounterErrorActions).length;

  const managingErrorValidator = () => {
    const assignedSiteError = () => {
      const resultsAssignedSitesErrors = encounterErrorSites.map((results) => (
        <span className="mr-2">
          {encounterErrorSites.length === 1
            ? `${results.rowPosition}`
            : `${results.rowPosition},`}
        </span>
      ));

      return (
        <div>
          {resultsAssignedSitesErrors.length >= 1 ? (
            <div>
              <span className="mr-2">
                {t("Users_managers.not_assigned_sites")}
              </span>
              <span>{`${t("Users_managers.line")} : `}</span>
              {resultsAssignedSitesErrors}
            </div>
          ) : (
            ""
          )}
        </div>
      );
    };

    const assignedActionError = () => {
      const resultsAssignedActionsErrors = encounterErrorActions.map(
        (results) => (
          <span className="mr-2">
            {encounterErrorActions.length === 1
              ? `${results.rowPosition}`
              : `${results.rowPosition},`}
          </span>
        ),
      );

      return (
        <div>
          {resultsAssignedActionsErrors.length >= 1 ? (
            <div>
              <span className="mr-2">
                {t("Users_managers.not_assigned_actions")}
              </span>

              <span>
                {resultsAssignedActionsErrors.length === 1
                  ? t("Users_managers.line")
                  : `${t("Users_managers.line")} : `}
              </span>
              {resultsAssignedActionsErrors}
            </div>
          ) : (
            ""
          )}
        </div>
      );
    };

    return (
      <Alert severity="error">
        {tryToCreateSomething ? (
          <div>
            <div>{assignedSiteError()}</div>
            <div>{assignedActionError()}</div>
          </div>
        ) : (
          ""
        )}
      </Alert>
    );
  };

  const managingShowErrors = () => {
    if (totalErrors >= 1 && tryToCreateSomething) {
      return true;
    } else {
      return false;
    }
  };

  if (multiLocationCreatedSuccess) {
    navigate("/");
    setTimeout(() => {
      cancel();
      setVerifyAllIsCreatedDefault();
    }, 2000);
  }

  const { openConfirmDialog, setOpenConfirmDialog } = useUnsavedChanges({
    currentData: principalInfoGT,
    originalData: principalInfoGTCopy,
  });

  const handleCloseClick = () => {
    if (
      JSON.stringify(principalInfoGT) !== JSON.stringify(principalInfoGTCopy)
    ) {
      setOpenConfirmDialog(true);
    } else {
      navigate("/locations");
      setTimeout(() => {
        cancel();
      }, 500);
    }
  };

  const handleConfirmClose = () => {
    setOpenConfirmDialog(false);
    navigate("/locations");
    setTimeout(() => {
      cancel();
    }, 500);
  };

  return (
    <div className="create-sites-container">
      <MenuCreateLocation
        createLocationStep={createLocationStep}
        t={t}
        setSteps={setSteps}
        multiLocationLoading={multiLocationLoading}
        openConfirmDialog={openConfirmDialog}
        handleCloseClick={handleCloseClick}
        handleConfirmClose={handleConfirmClose}
        setOpenConfirmDialog={setOpenConfirmDialog}
      />
      <div className="steps-container-create-loc">
        <div className="step-title">{t("Create_site.config_users.step")}</div>
        <div>
          <div className="step-title-create">
            {t("Create_site.config_users.config")}
          </div>
          <div className="design-scope">
            {t("Create_site.config_users.config_scope")}
          </div>
          {managingError()}
          {managingShowErrors() ? managingErrorValidator() : undefined}
          <div className="create-site-form" style={{ background: "#fff" }}>
            <Box sx={{ height: 420, width: 1 }}>
              <ThemeProvider theme={themeInarix(locale)}>
                <DataGridPro
                  disableRowSelectionOnClick={true}
                  loading={multiLocationLoading}
                  rows={rowsAlpha.length === 0 ? [] : rowsAlpha}
                  columns={
                    rawColumns.length === 0
                      ? [
                          {
                            field: "id",
                            sortable: true,
                            width: 100,
                          },
                        ]
                      : rawColumns
                  }
                  onColumnOrderChange={() => {
                    setColumnIndex(apiRef.current.getAllColumns());
                  }}
                  onColumnWidthChange={() =>
                    setColumnIndex(apiRef.current.getAllColumns())
                  }
                  editMode="row"
                  rowModesModel={rowModesModel}
                  onRowModesModelChange={handleRowModesModelChange}
                  onRowEditStop={handleRowEditStop}
                  processRowUpdate={processRowUpdate}
                  pageSizeOptions={[25, 50, 100]}
                  slots={{
                    footer: editToolbar as any["footer"],
                  }}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 25 } },
                  }}
                  apiRef={apiRef}
                  rowHeight={70}
                />
              </ThemeProvider>
            </Box>
            <Button
              disabled={multiLocationLoading}
              onClick={() => {
                if (totalErrors >= 1) {
                  tryToCreate(true);
                }
                if (totalErrors === 0) {
                  tryToCreate(false);
                  createLocations(userId as string, token, realmId);
                }
              }}
              sx={{ textTransform: "none", width: "79px", marginTop: "24px" }}
              variant="contained"
            >
              {t("Create_site.config_users.create")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepConfigCreateUsers;
